import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  Shield,
  Smartphone,
  LogOut,
  Copy,
  Download,
  Key,
  Fingerprint,
  ChevronRight,
  Mail,
  Check,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { authApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";

// Session item component
function SessionItem({ device, location, lastActive, isCurrent }) {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg ${
        isCurrent
          ? "bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800"
          : "bg-muted/50"
      }`}
    >
      <div className="flex items-center gap-3">
        <Smartphone className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="font-medium text-sm">{device}</p>
          <p className="text-xs text-muted-foreground">
            {location} · {lastActive}
          </p>
        </div>
      </div>
      {isCurrent ? (
        <Badge className="bg-emerald-500 text-white border-0">
          {t("common.active")}
        </Badge>
      ) : (
        <Button variant="ghost" size="sm">
          {t("settings.security.revokeSession")}
        </Button>
      )}
    </div>
  );
}

export const Security = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuthStore();
  const isMobile = useIsMobile();

  const [password, setPassword] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // 2FA state
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState("");
  const [twoFactorQR, setTwoFactorQR] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Get user role
  const userRole = user?.organisationRole || user?.role || "member";
  const isAdmin = ["owner", "admin"].includes(userRole);

  // ============================================
  // TanStack Query - Mutations
  // ============================================

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }) =>
      authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      toast.success("Password changed successfully");
      setPassword({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message || "Failed to change password",
      );
    },
  });

  // Setup 2FA mutation
  const setup2FAMutation = useMutation({
    mutationFn: () => authApi.setup2FA(),
    onSuccess: (response) => {
      const data = response.data?.data || response.data;
      setTwoFactorSecret(data.secret);
      setTwoFactorQR(data.qrCode);
      setShow2FADialog(true);
    },
    onError: () => {
      toast.error(t("settings.security.setup2FAError"));
    },
  });

  // Verify 2FA mutation
  const verify2FAMutation = useMutation({
    mutationFn: (code) => authApi.verify2FA(code),
    onSuccess: () => {
      updateUser({ ...user, twoFactorEnabled: true });
      toast.success(t("settings.security.twoFactorEnabledSuccess"));
      setShow2FADialog(false);
      setVerificationCode("");
      setTwoFactorSecret("");
      setTwoFactorQR("");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.security.invalidCode"),
      );
    },
  });

  // Disable 2FA mutation
  const disable2FAMutation = useMutation({
    mutationFn: () => authApi.disable2FA(),
    onSuccess: () => {
      updateUser({ ...user, twoFactorEnabled: false });
      toast.success(t("settings.security.twoFactorDisabledSuccess"));
    },
    onError: () => {
      toast.error(t("settings.security.disable2FAError"));
    },
  });

  // Set 2FA method mutation
  const setTwoFactorMethodMutation = useMutation({
    mutationFn: (method) => authApi.setTwoFactorMethod(method),
    onSuccess: (response) => {
      const data = response.data?.data || response.data;
      updateUser({ ...user, twoFactorMethod: data.method });
      toast.success(
        data.message || t("settings.security.twoFactorMethodUpdated"),
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          t(
            "settings.security.twoFactorMethodError",
            "Failed to update 2FA method",
          ),
      );
    },
  });

  // Revoke all sessions mutation
  const revokeAllSessionsMutation = useMutation({
    mutationFn: () => authApi.logoutAll(),
    onSuccess: () => {
      toast.success(t("settings.security.revokeAllSessionsSuccess"));
    },
    onError: () => {
      toast.error(t("settings.security.revokeAllSessionsError"));
    },
  });

  // Subject-access export: download all personal data we hold as JSON
  const exportDataMutation = useMutation({
    mutationFn: () => authApi.exportMyData(),
    onSuccess: (response) => {
      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/json" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `escribe-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(t("settings.downloadMyDataSuccess"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("settings.downloadMyDataError"),
      );
    },
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: () => authApi.deleteAccount(),
    onSuccess: () => {
      toast.success(t("settings.deleteAccountSuccess"));
      logout();
      navigate("/login");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("settings.deleteAccountError"),
      );
    },
  });

  // Derived states
  const loading =
    changePasswordMutation.isPending || revokeAllSessionsMutation.isPending;
  const enabling2FA =
    setup2FAMutation.isPending ||
    verify2FAMutation.isPending ||
    disable2FAMutation.isPending ||
    setTwoFactorMethodMutation.isPending;
  const deleting = deleteAccountMutation.isPending;

  const handlePasswordChange = (e) => {
    e.preventDefault();

    if (password.newPassword !== password.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (password.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: password.currentPassword,
      newPassword: password.newPassword,
    });
  };

  const handleEnable2FA = () => {
    setup2FAMutation.mutate();
  };

  const handleVerify2FA = () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t("settings.security.invalidCode"));
      return;
    }
    verify2FAMutation.mutate(verificationCode);
  };

  const handleDisable2FA = () => {
    disable2FAMutation.mutate();
  };

  const handleSetTwoFactorMethod = (method) => {
    if (method === user?.twoFactorMethod) return;
    setTwoFactorMethodMutation.mutate(method);
  };

  const handleRevokeAllSessions = () => {
    revokeAllSessionsMutation.mutate();
  };

  const handleDeleteAccount = () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error(t("settings.deleteAccountConfirmError"));
      return;
    }
    deleteAccountMutation.mutate();
  };

  const renderButtonTitleForTwoFactor = () => {
    if (enabling2FA) {
      return t("common.loading");
    }
    return user?.twoFactorEnabled
      ? t("settings.security.disable2FA")
      : t("settings.security.enable2FA");
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page header with back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border hover:bg-accent touch-target"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("settings.security.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("settings.security.subtitle")}
          </p>
        </div>
      </div>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.security.changePassword")}</CardTitle>
          <CardDescription>
            {t("settings.security.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">
                {t("settings.security.currentPassword")}
              </Label>
              <Input
                id="currentPassword"
                type="password"
                value={password.currentPassword}
                onChange={(e) =>
                  setPassword((p) => ({
                    ...p,
                    currentPassword: e.target.value,
                  }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {t("settings.security.newPassword")}
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={password.newPassword}
                onChange={(e) =>
                  setPassword((p) => ({ ...p, newPassword: e.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                {t("settings.security.passwordRequirements")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {t("settings.security.confirmPassword")}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={password.confirmPassword}
                onChange={(e) =>
                  setPassword((p) => ({
                    ...p,
                    confirmPassword: e.target.value,
                  }))
                }
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                size={isMobile ? "mobile" : "default"}
                className={isMobile ? "w-full" : ""}
              >
                {loading
                  ? t("common.saving")
                  : t("settings.security.updatePassword")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.security.twoFactor")}</CardTitle>
          <CardDescription>
            {t("settings.security.twoFactorDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 2FA Status */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  user?.twoFactorEnabled
                    ? "bg-emerald-100 dark:bg-emerald-900/30"
                    : "bg-muted"
                }`}
              >
                <Shield
                  className={`h-5 w-5 ${
                    user?.twoFactorEnabled
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-muted-foreground"
                  }`}
                />
              </div>
              <div>
                <p className="font-medium">
                  {t("settings.security.twoFactor")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {user?.twoFactorEnabled
                    ? t("settings.security.twoFactorEnabled")
                    : t("settings.security.twoFactorDisabled")}
                </p>
              </div>
            </div>
            <Button
              variant={user?.twoFactorEnabled ? "outline" : "default"}
              size="sm"
              onClick={
                user?.twoFactorEnabled ? handleDisable2FA : handleEnable2FA
              }
              disabled={enabling2FA}
            >
              {renderButtonTitleForTwoFactor()}
            </Button>
          </div>

          {/* 2FA Method Selector - Only show when 2FA is enabled */}
          {user?.twoFactorEnabled && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  {t(
                    "settings.security.twoFactorMethod",
                    "Authentication Method",
                  )}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "settings.security.twoFactorMethodDesc",
                    "Choose how you want to verify your identity when signing in",
                  )}
                </p>

                {/* Method Options */}
                <div className="space-y-2">
                  {/* Email Option */}
                  <button
                    type="button"
                    onClick={() => handleSetTwoFactorMethod("email")}
                    disabled={enabling2FA}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      user?.twoFactorMethod === "email"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                        <Mail className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-sm">
                          {t("settings.security.methodEmail", "Email")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            "settings.security.methodEmailDesc",
                            "Receive a code via email",
                          )}
                        </p>
                      </div>
                    </div>
                    {user?.twoFactorMethod === "email" && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>

                  {/* TOTP Option */}
                  <button
                    type="button"
                    onClick={() => handleSetTwoFactorMethod("totp")}
                    disabled={enabling2FA}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      user?.twoFactorMethod === "totp"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                        <Smartphone className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-sm">
                          {t(
                            "settings.security.methodTotp",
                            "Authenticator App",
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            "settings.security.methodTotpDesc",
                            "Use Google Authenticator or similar",
                          )}
                        </p>
                      </div>
                    </div>
                    {user?.twoFactorMethod === "totp" && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>

                  {/* WebAuthn Option */}
                  <button
                    type="button"
                    onClick={() => handleSetTwoFactorMethod("webauthn")}
                    disabled={enabling2FA}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      user?.twoFactorMethod === "webauthn"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                        <Key className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-sm">
                          {t(
                            "settings.security.methodWebauthn",
                            "Security Key",
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            "settings.security.methodWebauthnDesc",
                            "Use a hardware key or passkey",
                          )}
                        </p>
                      </div>
                    </div>
                    {user?.twoFactorMethod === "webauthn" && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                </div>

                {/* Hint for WebAuthn */}
                {user?.twoFactorMethod !== "webauthn" && (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "settings.security.webauthnHint",
                      "To use a security key, first register one in Advanced Security settings.",
                    )}
                  </p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.security.activeSessions")}</CardTitle>
          <CardDescription>
            {t("settings.security.activeSessionsDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <SessionItem
            device={t("settings.security.currentSession")}
            location="Chrome on macOS"
            lastActive="Last active: Now"
            isCurrent
          />

          <Separator />

          <Button
            variant="outline"
            className="w-full"
            onClick={handleRevokeAllSessions}
            disabled={loading}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {loading
              ? t("common.loading")
              : t("settings.security.revokeAllSessions")}
          </Button>
        </CardContent>
      </Card>

      {/* Advanced Security Options (for admins) */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              {t("settings.security.advancedOptions")}
            </CardTitle>
            <CardDescription>
              {t("settings.security.advancedOptionsDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link
              to="/settings/sso"
              className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <Fingerprint className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{t("settings.sso.titleFull")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.sso.samlOidcAuth")}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Link>

            <Link
              to="/settings/advanced-security"
              className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <Key className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">
                    {t("security.advancedSecurity")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("security.advancedSecurityDesc")}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Privacy & your data */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.privacyDataTitle")}</CardTitle>
          <CardDescription>{t("settings.privacyDataDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Download className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium">{t("settings.downloadMyData")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("settings.downloadMyDataDesc")}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={exportDataMutation.isPending}
              onClick={() => exportDataMutation.mutate()}
            >
              {exportDataMutation.isPending
                ? t("common.loading")
                : t("settings.downloadMyData")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">
            {t("common.dangerZone")}
          </CardTitle>
          <CardDescription>{t("common.dangerZoneDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-destructive/10 rounded-lg">
            <div>
              <p className="font-medium">{t("common.deleteAccount")}</p>
              <p className="text-sm text-muted-foreground">
                {t("common.deleteAccountDesc")}
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
            >
              {t("common.deleteAccount")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FADialog} onOpenChange={setShow2FADialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("settings.security.setup2FA")}</DialogTitle>
            <DialogDescription>
              {t("settings.security.setup2FADesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {twoFactorQR && (
              <div className="flex justify-center">
                <img
                  src={twoFactorQR}
                  alt="2FA QR Code"
                  className="w-48 h-48 border rounded-lg"
                />
              </div>
            )}
            {twoFactorSecret && (
              <div className="space-y-2">
                <Label>{t("settings.security.manualEntry")}</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-muted rounded text-sm font-mono break-all">
                    {twoFactorSecret}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(twoFactorSecret);
                      toast.success(t("common.copied"));
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="verificationCode">
                {t("settings.security.verificationCode")}
              </Label>
              <Input
                id="verificationCode"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(
                    e.target.value.replace(/\D/g, "").slice(0, 6),
                  )
                }
                className="text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShow2FADialog(false)}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleVerify2FA}
              disabled={enabling2FA || verificationCode.length !== 6}
              size={isMobile ? "mobile" : "default"}
            >
              {enabling2FA
                ? t("common.loading")
                : t("settings.security.verify")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setDeleteConfirmText("");
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {t("settings.deleteAccountTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("settings.deleteAccountWarning")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
              <p className="text-sm font-medium text-destructive">
                {t("settings.deleteAccountDataWarning")}
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• {t("settings.deleteDataEnvelopes")}</li>
                <li>• {t("settings.deleteDataTemplates")}</li>
                <li>• {t("settings.deleteDataAPIKeys")}</li>
                <li>• {t("settings.deleteDataSettings")}</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deleteConfirm">
                {t("settings.deleteConfirmLabel")}
              </Label>
              <Input
                id="deleteConfirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t("placeholders.typeDelete")}
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteConfirmText("");
              }}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirmText !== "DELETE"}
              size={isMobile ? "mobile" : "default"}
            >
              {deleting
                ? t("common.loading")
                : t("settings.deleteAccountConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Security;
